'use client';

import { useEventListener } from '@woonstad/helpers-hooks';
import { hasValue } from '@woonstad/helpers-utils';
import clsx from 'clsx';
import { ForwardedRef, forwardRef, InputHTMLAttributes, useId, useImperativeHandle, useRef } from 'react';

import { BasicClose, BasicSearch } from '../../icons/basic';
import { ButtonPrimary } from '../buttons/ButtonPrimary';
import { labelRegularClasses } from '../typography/Typography';

type Props = {
  isOnDark?: boolean;
  placeholder?: string;
  label?: string;
  clearInputLabel: string;
  variant?: 'default' | 'large';
  value: string;
  setValue: (value: string) => void;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onEnter?: () => void;
  onClear?: () => void;
  searchButtonTabIndex?: number;
  onButtonClick?: (ev: React.MouseEvent<HTMLButtonElement>) => void;
}

export const SearchInput = forwardRef((
  {
    isOnDark = false,
    placeholder,
    label,
    clearInputLabel,
    variant = 'default',
    value,
    setValue,
    onChange,
    onBlur,
    onEnter,
    onClear,
    searchButtonTabIndex = 0,
    onButtonClick,
    ...inputProps
  }: Props & InputHTMLAttributes<HTMLInputElement>,
  outerRef: ForwardedRef<HTMLInputElement>,
) => {
  const innerRef = useRef<HTMLInputElement>(null);
  const inputId = useId();
  const inputLabel = label ?? placeholder;
  useImperativeHandle(outerRef, () => innerRef.current!, []);

  useEventListener(
    'keydown',
    (e) => {
      if (e.key === 'Escape') {
        innerRef.current?.blur();
      }

      if (e.key === 'Enter' && onEnter) {
        onEnter();
      }
    },
    innerRef,
  );

  const internalOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  const internalOnClear = () => {
    setValue('');
    innerRef.current?.focus();
  };

  return (
    <div
      data-testvalue={value}
      data-testlabel={placeholder}
      className={clsx(
        labelRegularClasses,
        'flex w-full items-center rounded-full has-[:focus]:outline has-[:focus]:outline-offset-2',
        {
          'h-10': variant === 'default',
          'h-14': variant === 'large',
          'bg-search-on-light has-[:focus]:outline-[-webkit-focus-ring-color] has-[:focus]:outline-offset-1': !isOnDark,
          'bg-search-on-dark has-[:focus]:outline-[white] has-[:focus]:outline-offset-2': isOnDark,
        },
      )}
    >
      {inputLabel ? (
        <label
          htmlFor={inputId}
          className="sr-only"
        >
          {inputLabel}
        </label>
      ) : null}
      <input
        {...inputProps}
        ref={innerRef}
        id={inputId}
        type="text"
        onChange={onChange ?? internalOnChange}
        onBlur={onBlur}
        placeholder={placeholder}
        value={value}
        className={clsx(
          'w-full rounded-full bg-[transparent] text-search outline-none placeholder:text-search-hint',
          {
            'h-10 px-4': variant === 'default',
            'h-14 px-6': variant === 'large',
          },
        )}
        data-testid="searchInput"
      />

      <div
        className={clsx('flex items-center gap-2', {
          'mr-2': variant === 'large',
        })}
      >
        {value && (
          <button
            type="button"
            onClick={onClear ?? internalOnClear}
            className="flex size-5 items-center justify-center rounded-full bg-search-icon text-search-icon"
            aria-label={'Wissen'}
          >
            <span className="sr-only">{clearInputLabel}</span>
            <BasicClose className="size-3" />
          </button>
        )}

        <div className={hasValue(onButtonClick) ? 'cursor-pointer' : 'pointer-events-none'}>
          <ButtonPrimary
            iconOnly
            icon={BasicSearch}
            overrideDisabledState={false}
          >
            <button
              type="button"
              tabIndex={searchButtonTabIndex}
              onClick={onButtonClick}
              aria-label={'Zoeken'}
            />
          </ButtonPrimary>
        </div>
      </div>
    </div >
  );
});

SearchInput.displayName = 'SearchInput';
